"use client";

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/fr";
import { useParams } from "next/navigation";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { SuccessMessage } from "@/app/(main)/(club)/mes-clubs/[id]/components/success-message";
import { useFlashMessage } from "@/app/(main)/(club)/mes-clubs/[id]/components/use-flash-message";
import { OtherDateTitle } from "@/app/(main)/(club)/mes-clubs/[id]/invitations/modal/participation/title";
import { meetingDaySchema } from "@/app/(main)/(club)/mes-clubs/[id]/schemas/club-settings-schema";
import { SettingsList } from "@/app/(main)/(club)/mes-clubs/components/[modify-club-settings]/settings-list";
import { DAYS_OF_WEEK } from "@/app/(main)/(club)/mes-clubs/components/[modify-club-settings]/utils/day-of-week";
import { FormActions } from "@/app/(security)/inscription/components/form-actions";
import { FormDatePicker } from "@/components/global/form/inputs/form-data-picker";
import { FormSelect } from "@/components/global/form/inputs/form-select";
import { FormTimePicker } from "@/components/global/form/inputs/form-time-picker";
import { useGetSettingsQuery } from "@/queries/theme/use-get-settings";
import { useUpdateSettings } from "@/queries/theme/use-update-settings";
import { useModalStore } from "@/stores/modal-store";
import { devLog } from "@/utils/dev-log";
dayjs.locale("fr");
export interface EditEventFormData {
  meetingDay: string;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  effectiveDate: Dayjs | null;
  notifyByEmail: boolean;
}
export const ClubSettingsModal = () => {
  const $ = _c(64);
  const {
    id: clubSlug
  } = useParams();
  const {
    close
  } = useModalStore();
  const {
    showFlashMessage
  } = useFlashMessage();
  const {
    mutate: updateSettings,
    isPending,
    isSuccess
  } = useUpdateSettings();
  const {
    data: settingsData,
    isLoading: isLoadingSettings,
    isError
  } = useGetSettingsQuery(clubSlug as string);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mode: "onSubmit",
      resolver: zodResolver(meetingDaySchema),
      defaultValues: {
        meetingDay: "",
        startTime: null,
        endTime: null,
        effectiveDate: null,
        notifyByEmail: false
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const methods = useForm(t0);
  const {
    handleSubmit,
    formState: t1
  } = methods;
  const {
    errors,
    isSubmitted,
    isDirty
  } = t1;
  let t2;
  if ($[1] !== close || $[2] !== clubSlug || $[3] !== isDirty || $[4] !== showFlashMessage || $[5] !== updateSettings) {
    t2 = async data => {
      ;
      try {
        if (!isDirty) {
          showFlashMessage("Aucune modification apport\xE9e", "warning2", "mes-clubs-calendar");
          close();
          return;
        }
        const dayWeekMap = {
          monday: 1,
          tuesday: 2,
          wednesday: 3,
          thursday: 4,
          friday: 5,
          saturday: 6,
          sunday: 7
        };
        const startDate = dayjs(data.effectiveDate).format("YYYY-MM-DDTHH:mm:ssZ");
        const openingTime = dayjs(data.startTime).format("YYYY-MM-DDTHH:mm:ssZ");
        const closingTime = dayjs(data.endTime).format("YYYY-MM-DDTHH:mm:ssZ");
        const formattedData = {
          dayWeek: dayWeekMap[data.meetingDay as keyof typeof dayWeekMap],
          startDate,
          openingTime: `${openingTime}`,
          closingTime: `${closingTime}`,
          notifyByEmail: data.notifyByEmail
        };
        devLog(formattedData);
        updateSettings({
          clubSlug,
          settings: formattedData
        });
      } catch (t3) {
        const error = t3;
        console.error("Erreur lors de la modification:", error);
      }
    };
    $[1] = close;
    $[2] = clubSlug;
    $[3] = isDirty;
    $[4] = showFlashMessage;
    $[5] = updateSettings;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const onSubmit = t2;
  if (isSuccess) {
    let t3;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <SuccessMessage title={"Jour et/ou horaires modifi\xE9s avec succ\xE8s !"} />;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  let t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <OtherDateTitle title={"Modifier le jour et/ou les horaires de mes r\xE9unions Club d'Affaires"} />;
    t4 = <Alert variant="filled" severity="warning">Les réunions que vous avez modifiées par le passé ne seront pas prises en compte par le changement de jour/d'horaire. Vous recevrez un email listant ces réunions à ajuster manuellement.</Alert>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  let t5;
  if ($[10] !== handleSubmit || $[11] !== onSubmit) {
    t5 = handleSubmit(onSubmit);
    $[10] = handleSubmit;
    $[11] = onSubmit;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== errors.meetingDay || $[14] !== isSubmitted) {
    t6 = t7 => {
      const {
        field
      } = t7;
      return <FormSelect {...field} label={"Jour de r\xE9union"} error={errors.meetingDay?.message} isValid={isSubmitted && !errors.meetingDay} required={true} styles={{
        wrapper: {
          className: "w-full"
        },
        select: {
          className: "w-full bg-white autofill:bg-transparent rounded-md appearance-none mr-0 cursor-pointer border focus:outline-none focus:ring-0 pl-4 pr-10 h-12"
        }
      }}><option value="">Sélectionner un jour...</option>{DAYS_OF_WEEK.map(_temp)}</FormSelect>;
    };
    $[13] = errors.meetingDay;
    $[14] = isSubmitted;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== methods.control || $[17] !== t6) {
    t7 = <div className="relative w-full"><div className="relative"><Controller name="meetingDay" control={methods.control} render={t6} /></div></div>;
    $[16] = methods.control;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== errors.startTime || $[20] !== isSubmitted) {
    t8 = t9 => {
      const {
        field: t10
      } = t9;
      const {
        onChange,
        value
      } = t10;
      return <FormTimePicker name="startTime" newInput={false} label={"Heure de d\xE9but"} required={true} error={errors.startTime?.message} isValid={isSubmitted && !errors.startTime} value={value} onChange={onChange} />;
    };
    $[19] = errors.startTime;
    $[20] = isSubmitted;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== methods.control || $[23] !== t8) {
    t9 = <Controller name="startTime" control={methods.control} render={t8} />;
    $[22] = methods.control;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== errors.endTime || $[26] !== isSubmitted) {
    t10 = t11 => {
      const {
        field: t12
      } = t11;
      const {
        onChange: onChange_0,
        value: value_0
      } = t12;
      return <FormTimePicker name="endTime" newInput={false} label="Heure de fin" required={true} error={errors.endTime?.message} isValid={isSubmitted && !errors.endTime} value={value_0} onChange={onChange_0} />;
    };
    $[25] = errors.endTime;
    $[26] = isSubmitted;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== methods.control || $[29] !== t10) {
    t11 = <Controller name="endTime" control={methods.control} render={t10} />;
    $[28] = methods.control;
    $[29] = t10;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t11 || $[32] !== t9) {
    t12 = <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{t9}{t11}</div>;
    $[31] = t11;
    $[32] = t9;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== errors.effectiveDate || $[35] !== isSubmitted) {
    t13 = t14 => {
      const {
        field: t15
      } = t14;
      const {
        onChange: onChange_1,
        value: value_1
      } = t15;
      return <FormDatePicker name="effectiveDate" label={"Appliquer la modification \xE0 partir du"} required={true} error={errors.effectiveDate?.message} isValid={isSubmitted && !errors.effectiveDate} value={value_1} onChange={onChange_1} minDate={dayjs().add(1, "day")} />;
    };
    $[34] = errors.effectiveDate;
    $[35] = isSubmitted;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== methods.control || $[38] !== t13) {
    t14 = <Controller name="effectiveDate" control={methods.control} render={t13} />;
    $[37] = methods.control;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== methods.control) {
    t15 = <div className="flex flex-col"><Controller name={"notifyByEmail" as keyof EditEventFormData} control={methods.control} render={_temp2} /></div>;
    $[40] = methods.control;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t12 || $[43] !== t14 || $[44] !== t15 || $[45] !== t7) {
    t16 = <div className="flex flex-col pt-4 pb-12 rounded-lg bg-opacity-5 space-y-4">{t7}{t12}{t14}{t15}</div>;
    $[42] = t12;
    $[43] = t14;
    $[44] = t15;
    $[45] = t7;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== close || $[48] !== isPending) {
    t17 = <FormActions isPending={isPending} closeModal={close} submitLabel="Valider" />;
    $[47] = close;
    $[48] = isPending;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== t16 || $[51] !== t17 || $[52] !== t5) {
    t18 = <form onSubmit={t5} className="space-y-6">{t16}{t17}</form>;
    $[50] = t16;
    $[51] = t17;
    $[52] = t5;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== methods || $[55] !== t18) {
    t19 = <FormProvider {...methods}>{t18}</FormProvider>;
    $[54] = methods;
    $[55] = t18;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== isError || $[58] !== isLoadingSettings || $[59] !== settingsData) {
    t20 = <SettingsList settingsData={settingsData} isLoadingSettings={isLoadingSettings} isError={isError} />;
    $[57] = isError;
    $[58] = isLoadingSettings;
    $[59] = settingsData;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  let t21;
  if ($[61] !== t19 || $[62] !== t20) {
    t21 = <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr"><div className="transition-[max-height] duration-300 ease-in-out max-h-[1000px]"><div className="p-6">{t3}{t4}{t19}{t20}</div></div></LocalizationProvider>;
    $[61] = t19;
    $[62] = t20;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  return t21;
};
function _temp(day) {
  return <option key={day.value} value={day.value}>{day.label}</option>;
}
function _temp2(t0) {
  const {
    field: t1
  } = t0;
  const {
    onChange: onChange_2,
    value: value_2
  } = t1;
  return <FormControlLabel control={<Checkbox checked={!!value_2} onChange={e => onChange_2(e.target.checked)} sx={{
    borderRadius: "50%",
    "& .MuiSvgIcon-root": {
      borderRadius: "50%"
    },
    "&.Mui-checked": {
      color: "#EE4540",
      "& .MuiSvgIcon-root": {
        borderRadius: "50%"
      }
    }
  }} />} label={<span className="italic text-sm">Notifier les membres par email</span>} />;
}